import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  ChevronDown,
  HelpCircle,
  Info,
  Lock,
  Pin,
  TriangleAlert,
  Wrench,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { useParams } from "react-router-dom";
import type {
  DailyUptime,
  Incident,
  IncidentStyle,
  OverallStatus,
  PublicMonitor,
  PublicStatusGroup,
  StatusValue,
} from "@shared/types";
import { STATUS } from "@shared/types";
import { DailyBars } from "@/components/public/daily-bars";
import { EventCalendar } from "@/components/public/event-calendar";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError, authStatusPage, getPublicStatus } from "@/lib/api";
import { queryKeys } from "@/lib/queries";
import { cn, formatDateTime, formatLatency, formatRelative, formatUptime } from "@/lib/utils";

interface HeroStyle {
  label: string;
  sublabel: string;
  Icon: LucideIcon;
  className: string;
  iconClassName: string;
}

const HERO_BY_STATUS: Record<OverallStatus, HeroStyle> = {
  up: {
    label: "Everything is running smoothly",
    sublabel: "All systems are operating as expected.",
    Icon: CheckCircle2,
    className: "bg-status-up/10",
    iconClassName: "text-status-up",
  },
  degraded: {
    label: "Partial outage",
    sublabel: "Some systems are experiencing issues.",
    Icon: TriangleAlert,
    className: "bg-status-pending/10",
    iconClassName: "text-status-pending",
  },
  down: {
    label: "Major outage",
    sublabel: "Multiple systems are currently unavailable.",
    Icon: XCircle,
    className: "bg-status-down/10",
    iconClassName: "text-status-down",
  },
  maintenance: {
    label: "Under maintenance",
    sublabel: "Scheduled maintenance is in progress.",
    Icon: Wrench,
    className: "bg-status-maintenance/10",
    iconClassName: "text-status-maintenance",
  },
  unknown: {
    label: "No monitors yet",
    sublabel: "This page has no components to report on.",
    Icon: HelpCircle,
    className: "bg-muted/60",
    iconClassName: "text-muted-foreground",
  },
};

const INCIDENT_STYLE: Record<IncidentStyle, string> = {
  info: "bg-status-maintenance/10",
  warning: "bg-status-pending/10",
  danger: "bg-status-down/10",
  success: "bg-status-up/10",
};

const ROW_ICON: Record<StatusValue, { Icon: LucideIcon; className: string }> = {
  0: { Icon: XCircle, className: "text-status-down" },
  1: { Icon: CheckCircle2, className: "text-status-up" },
  2: { Icon: TriangleAlert, className: "text-status-pending" },
  3: { Icon: Wrench, className: "text-status-maintenance" },
};

function uptimeColor(uptime: number | null): string {
  if (uptime == null || uptime >= 0.99) return "text-muted-foreground";
  if (uptime < 0.95) return "text-status-down";
  return "text-status-pending";
}

function isImageUrl(icon: string): boolean {
  try {
    const url = new URL(icon);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

/** "May 2026 – Aug 2026" for the 90-day window the bars display. */
function barRangeLabel(): string {
  const format = (date: Date) => date.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  const start = new Date(Date.now() - 89 * 86_400_000);
  return `${format(start)} – ${format(new Date())}`;
}

function PublicPageLoading() {
  return (
    <main className="min-h-dvh bg-background text-foreground">
      <div className="container mx-auto max-w-3xl space-y-6 px-4 py-10 md:py-14">
        <div className="flex items-center gap-4">
          <Skeleton className="size-10 rounded-lg" />
          <Skeleton className="h-7 w-48" />
        </div>
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-96 w-full rounded-xl" />
        <Skeleton className="h-72 w-full rounded-xl" />
      </div>
    </main>
  );
}

function NotFound() {
  return (
    <main className="grid min-h-dvh place-items-center bg-background px-6 text-center text-sm text-muted-foreground">
      This status page does not exist.
    </main>
  );
}

function PasswordGate({ slug, error }: { slug: string; error: ApiError }) {
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const mutation = useMutation({
    mutationFn: (value: string) => authStatusPage(slug, value),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.publicStatus(slug) });
    },
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!password || mutation.isPending) return;
    mutation.mutate(password);
  }

  const inlineError = mutation.error instanceof ApiError ? mutation.error.message : mutation.error?.message;

  return (
    <main className="relative grid min-h-dvh place-items-center overflow-hidden bg-background px-4 text-foreground">
      <div className="pointer-events-none absolute -top-48 left-1/2 size-96 -translate-x-1/2 rounded-full bg-status-up/5 blur-3xl" />
      <Card className="relative w-full max-w-sm shadow-xl shadow-black/5">
        <CardHeader className="items-center text-center">
          <div className="mb-2 grid size-12 place-items-center rounded-full bg-muted">
            <Lock className="size-5 text-muted-foreground" />
          </div>
          <CardTitle className="text-xl">This status page is protected</CardTitle>
          <CardDescription>Enter the password to view its current status.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="status-password">Password</Label>
              <Input
                id="status-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  if (mutation.error) mutation.reset();
                }}
                aria-invalid={Boolean(inlineError)}
              />
            </div>
            {inlineError && <p className="text-sm text-destructive">{inlineError}</p>}
            <Button className="w-full" type="submit" disabled={!password || mutation.isPending}>
              {mutation.isPending ? "Unlocking…" : "View status"}
            </Button>
          </form>
          {!inlineError && <p className="sr-only">{error.message}</p>}
        </CardContent>
      </Card>
    </main>
  );
}

interface GroupSummary {
  status: StatusValue | null;
  uptime90d: number | null;
  daily: DailyUptime[];
}

/** Worst status wins; uptime is the mean across members; daily series merged per day. */
function summarizeGroup(monitors: PublicMonitor[]): GroupSummary {
  const severity: StatusValue[] = [STATUS.DOWN, STATUS.PENDING, STATUS.MAINTENANCE, STATUS.UP];
  const statuses = monitors.map((m) => m.status).filter((s): s is StatusValue => s != null);
  const status = severity.find((s) => statuses.includes(s)) ?? null;

  const uptimes = monitors.map((m) => m.uptime90d).filter((v): v is number => v != null);
  const uptime90d = uptimes.length > 0 ? uptimes.reduce((a, b) => a + b, 0) / uptimes.length : null;

  // All monitors share the same server-generated 90-day window, so merge by day key.
  const byDay = new Map<string, { sum: number; count: number; down: number }>();
  const order: string[] = [];
  for (const monitor of monitors) {
    for (const day of monitor.daily) {
      let entry = byDay.get(day.day);
      if (!entry) {
        entry = { sum: 0, count: 0, down: 0 };
        byDay.set(day.day, entry);
        order.push(day.day);
      }
      if (day.uptime != null) {
        entry.sum += day.uptime;
        entry.count += 1;
      }
      entry.down += day.down;
    }
  }
  const daily = order.map((day) => {
    const entry = byDay.get(day)!;
    return { day, uptime: entry.count > 0 ? entry.sum / entry.count : null, down: entry.down };
  });

  return { status, uptime90d, daily };
}

function MonitorRow({ monitor, incidents }: { monitor: PublicMonitor; incidents: Incident[] }) {
  const icon = monitor.status != null ? ROW_ICON[monitor.status] : null;
  const RowIcon = icon?.Icon ?? HelpCircle;
  const details = [
    monitor.avgLatency24h != null ? `Avg response ${formatLatency(monitor.avgLatency24h)}` : null,
    `24h uptime ${formatUptime(monitor.uptime24h)}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="space-y-2.5 px-5 py-4 sm:px-6">
      <div className="flex items-center gap-2">
        <RowIcon className={cn("size-4.5 shrink-0", icon?.className ?? "text-muted-foreground")} />
        <span className="min-w-0 truncate text-sm font-medium">{monitor.name}</span>
        <span title={details} className="cursor-default">
          <Info className="size-3.5 text-muted-foreground/60" />
        </span>
        <span className={cn("ml-auto shrink-0 text-sm tabular-nums", uptimeColor(monitor.uptime90d))}>
          {formatUptime(monitor.uptime90d)} uptime
        </span>
      </div>
      <DailyBars daily={monitor.daily} incidents={incidents} />
    </div>
  );
}

/** Sections with more than two components start collapsed so the page stays scannable. */
function ComponentGroup({ group, incidents }: { group: PublicStatusGroup; incidents: Incident[] }) {
  const [open, setOpen] = useState(group.monitors.length <= 2);
  const summary = useMemo(() => summarizeGroup(group.monitors), [group.monitors]);
  const icon = summary.status != null ? ROW_ICON[summary.status] : null;
  const GroupIcon = icon?.Icon ?? HelpCircle;
  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-2 px-5 py-3.5 text-left transition-colors hover:bg-muted/40 sm:px-6"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <GroupIcon className={cn("size-4.5 shrink-0", icon?.className ?? "text-muted-foreground")} />
        <span className="min-w-0 truncate text-sm font-medium">{group.name}</span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {group.monitors.length} component{group.monitors.length === 1 ? "" : "s"}
        </span>
        <ChevronDown className={cn("size-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")} />
        <span className={cn("ml-auto shrink-0 text-sm tabular-nums", uptimeColor(summary.uptime90d))}>
          {formatUptime(summary.uptime90d)} uptime
        </span>
      </button>
      {open ? (
        <div className="divide-y divide-border/60">
          {group.monitors.map((monitor) => (
            <MonitorRow key={monitor.id} monitor={monitor} incidents={incidents} />
          ))}
        </div>
      ) : (
        <div className="px-5 pb-4 sm:px-6">
          <DailyBars daily={summary.daily} incidents={incidents} />
        </div>
      )}
    </div>
  );
}

function StatusPageContent({ slug }: { slug: string }) {
  const query = useQuery({
    queryKey: queryKeys.publicStatus(slug),
    queryFn: () => getPublicStatus(slug),
    refetchInterval: 30_000,
    retry: (count, err) => !(err instanceof ApiError && (err.status === 401 || err.status === 404)) && count < 2,
  });

  useEffect(() => {
    if (query.data) document.title = `${query.data.page.title} · Status`;
  }, [query.data]);

  if (query.isLoading) return <PublicPageLoading />;
  if (query.error instanceof ApiError && query.error.status === 404) return <NotFound />;
  if (query.error instanceof ApiError && query.error.status === 401 && query.error.needsPassword) {
    return <PasswordGate slug={slug} error={query.error} />;
  }
  if (query.error || !query.data) {
    return (
      <main className="grid min-h-dvh place-items-center bg-background px-6 text-center">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Unable to load this status page.</p>
          <Button variant="outline" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </div>
      </main>
    );
  }

  const { page, overallStatus, incidents, groups, generatedAt } = query.data;
  const hero = HERO_BY_STATUS[overallStatus];
  const HeroIcon = hero.Icon;
  const activeIncidents = incidents.filter((incident) => incident.resolvedAt == null);

  let pageIcon: ReactNode = null;
  if (page.icon && isImageUrl(page.icon)) {
    pageIcon = <img src={page.icon} alt="" className="size-9 shrink-0 rounded-lg object-cover" />;
  } else if (page.icon) {
    pageIcon = (
      <span className="shrink-0 text-2xl leading-9" aria-hidden="true">
        {page.icon}
      </span>
    );
  }

  return (
    <main className="min-h-dvh bg-background text-foreground">
      <div className="container mx-auto max-w-3xl space-y-6 px-4 py-10 md:py-14">
        <header className="flex items-center gap-3">
          {pageIcon}
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold tracking-tight">{page.title}</h1>
            {page.description && <p className="truncate text-sm text-muted-foreground">{page.description}</p>}
          </div>
          <div className="ml-auto shrink-0">
            <ThemeToggle />
          </div>
        </header>

        <section className={cn("flex items-start gap-3 rounded-xl p-5", hero.className)}>
          <HeroIcon className={cn("mt-0.5 size-5 shrink-0", hero.iconClassName)} />
          <div className="min-w-0">
            <p className="font-semibold">{hero.label}</p>
            <p className="mt-0.5 text-sm text-muted-foreground">{hero.sublabel}</p>
          </div>
          <p className="ml-auto shrink-0 self-center text-xs text-muted-foreground">
            Updated {formatRelative(generatedAt)}
          </p>
        </section>

        {activeIncidents.map((incident) => (
          <article
            key={incident.id}
            className={cn("rounded-xl p-5", INCIDENT_STYLE[incident.style])}
          >
            <div className="flex items-start gap-2">
              {incident.pinned && <Pin className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-label="Pinned" />}
              <div className="min-w-0">
                <h2 className="font-medium">{incident.title}</h2>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">{incident.content}</p>
                <p className="mt-2 text-xs text-muted-foreground">Posted {formatDateTime(incident.createdAt)}</p>
              </div>
            </div>
          </article>
        ))}

        <section className="overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
          <header className="flex items-center justify-between border-b px-5 py-4 sm:px-6">
            <h2 className="font-semibold">System status</h2>
            <span className="text-sm text-muted-foreground tabular-nums">{barRangeLabel()}</span>
          </header>
          {groups.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">No components on this page yet.</p>
          ) : (
            <div className="divide-y">
              {groups.map((group) => (
                <ComponentGroup key={group.id} group={group} incidents={incidents} />
              ))}
            </div>
          )}
        </section>

        <EventCalendar incidents={incidents} />

        <footer className="space-y-1 pb-4 pt-2 text-center text-xs text-muted-foreground">
          {page.showPoweredBy && (
            <p>
              Powered by <span className="font-medium text-foreground">Vigil</span>
            </p>
          )}
          <p>Auto-refreshes every 30 seconds · Uptime computed from check results</p>
        </footer>
      </div>
    </main>
  );
}

export default function PublicStatusPage({ slugOverride }: { slugOverride?: string }) {
  const params = useParams<{ slug: string }>();
  const slug = slugOverride ?? params.slug;
  if (!slug) return <NotFound />;
  return <StatusPageContent slug={slug} />;
}
